import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));


    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLElement} dualSlider - The dual slider element
 */

/**
 * Handles price facet functionality with dual slider integration
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#initializeDualSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#cleanupDualSlider();
  }

  /**
   * Initialize dual slider integration
   */
  #initializeDualSlider() {
    const dualSlider = this.querySelector('dual-slider');
    if (!dualSlider) return;

    // Listen to slider events
    dualSlider.addEventListener('slider', this.#handleSliderChange);
  }

  /**
   * Cleanup dual slider event listeners
   */
  #cleanupDualSlider() {
    const dualSlider = this.querySelector('dual-slider');
    if (dualSlider) {
      dualSlider.removeEventListener('slider', this.#handleSliderChange);
    }
  }

  /**
   * Handle slider value changes
   * @param {CustomEvent} event - The slider event
   */
  #handleSliderChange = (event) => {
    const { low, high } = event.detail;
    const { minInput, maxInput } = this.refs;

    // Update hidden form inputs
    if (minInput) {
      minInput.value = low.toString();
    }
    if (maxInput) {
      maxInput.value = high.toString();
    }

    // Trigger filter update with debouncing
    this.#debounceUpdate();
  };

  /**
   * Debounced update to prevent too many API calls
   */
  #debounceUpdate = debounce(() => {
    this.updatePriceFilterAndResults();
  }, 300);

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}
if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}
/**
 * 双向滑动滑块组件
 * @extends {Component}
 */
class DualSlider extends Component {
  constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // 属性
      /** @type {number} */ this._min = 0;
      /** @type {number} */ this._max = 100;
      /** @type {number} */ this._low = 0;
      /** @type {number} */ this._high = 205;
      /** @type {number} */ this._step = 1;
      
      // 拖拽状态
      /** @type {boolean} */ this.isDragging = false;
      /** @type {HTMLElement | null} */ this.activeThumb = null;
      
      // DOM 元素引用
      /** @type {HTMLElement | null} */ this.container = null;
      /** @type {HTMLElement | null} */ this.track = null;
      /** @type {HTMLElement | null} */ this.range = null;
      /** @type {HTMLElement | null} */ this.lowThumb = null;
      /** @type {HTMLElement | null} */ this.highThumb = null;
      /** @type {HTMLElement | null} */ this.lowLabel = null;
      /** @type {HTMLElement | null} */ this.highLabel = null;
      
      // 拖拽计算用
      /** @type {number} */ this.trackLeft = 0;
      /** @type {number} */ this.trackWidth = 0;
      
      // 绑定方法
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }
  
  /**
   * 观察的属性列表
   * @returns {string[]}
   */
  static get observedAttributes() {
      return ['min', 'max', 'low', 'high', 'step'];
  }
  
  /**
   * 属性变化回调
   * @param {string} name - 属性名
   * @param {string | null} oldValue - 旧值
   * @param {string | null} newValue - 新值
   */
  attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== newValue && newValue !== null) {
          switch (name) {
              case 'min':
                  this._min = parseFloat(newValue) || 0;
                  break;
              case 'max':
                  this._max = parseFloat(newValue) || 100;
                  break;
              case 'low':
                  this._low = parseFloat(newValue) || 0;
                  break;
              case 'high':
                  this._high = parseFloat(newValue) || 75;
                  break;
              case 'step':
                  this._step = parseFloat(newValue) || 1;
                  break;
          }
          this.updateSlider();
      }
  }
  
  /**
   * 组件连接到 DOM 时的回调
   */
  connectedCallback() {
      const template = /** @type {HTMLTemplateElement | null} */ (
          document.getElementById('dual-slider-template')
      );
      if (!template || !this.shadowRoot) return;
      
      this.shadowRoot.appendChild(template.content.cloneNode(true));
      
      this.container = this.shadowRoot.querySelector('.slider-container');
      this.track = this.shadowRoot.querySelector('.slider-track');
      this.range = this.shadowRoot.querySelector('.slider-range');
      this.lowThumb = this.shadowRoot.querySelector('[data-thumb="low"]');
      this.highThumb = this.shadowRoot.querySelector('[data-thumb="high"]');
      this.lowLabel = this.lowThumb?.querySelector('.slider-label') || null;
      this.highLabel = this.highThumb?.querySelector('.slider-label') || null;
      
      this.setupEventListeners();
      this.updateSlider();
  }
  
  /**
   * 设置事件监听器
   */
  setupEventListeners() {
      if (!this.lowThumb || !this.highThumb) return;
      
      // 鼠标事件
      this.lowThumb.addEventListener('mousedown', this.handleMouseDown);
      this.highThumb.addEventListener('mousedown', this.handleMouseDown);
      
      // 触摸事件
      this.lowThumb.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      this.highThumb.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      
      // 全局事件
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      document.addEventListener('touchend', this.handleTouchEnd);
  }
  
  /**
   * 鼠标按下事件处理
   * @param {MouseEvent} e - 鼠标事件
   */
  handleMouseDown(e) {
      e.preventDefault();
      const target = /** @type {HTMLElement} */ (e.target);
      this.startDrag(target, e.clientX);
  }
  
  /**
   * 触摸开始事件处理
   * @param {TouchEvent} e - 触摸事件
   */
      handleTouchStart(e) {
        e.preventDefault();
        const touch = e.touches[0];
        if (!touch) return;
        const target = /** @type {HTMLElement} */ (e.target);
        this.startDrag(target, touch.clientX);
    }
  
  /**
   * 开始拖拽
   * @param {HTMLElement} thumb - 拖拽的滑块元素
   * @param {number} clientX - 客户端 X 坐标
   */
  startDrag(thumb, clientX) {
      if (!this.track) return;
      
      this.isDragging = true;
      this.activeThumb = thumb;
      thumb.classList.add('dragging');
      
      const rect = this.track.getBoundingClientRect();
      this.trackLeft = rect.left;
      this.trackWidth = rect.width;
  }
  
  /**
   * 鼠标移动事件处理
   * @param {MouseEvent} e - 鼠标事件
   */
  handleMouseMove(e) {
      if (this.isDragging) {
          this.updateValue(e.clientX);
      }
  }
  
  /**
   * 触摸移动事件处理
   * @param {TouchEvent} e - 触摸事件
   */
      handleTouchMove(e) {
        if (this.isDragging) {
            const touch = e.touches[0];
            if (!touch) return;
            e.preventDefault();
            this.updateValue(touch.clientX);
        }
    }
  
  /**
   * 更新滑块值
   * @param {number} clientX - 客户端 X 坐标
   */
  updateValue(clientX) {
      if (!this.activeThumb) return;
      
      const percent = Math.max(0, Math.min(1, (clientX - this.trackLeft) / this.trackWidth));
      const value = this._min + percent * (this._max - this._min);
      const steppedValue = Math.round(value / this._step) * this._step;
      
      const thumbType = this.activeThumb.getAttribute('data-thumb');
      if (thumbType === 'low') {
          this._low = Math.min(steppedValue, this._high);
      } else if (thumbType === 'high') {
          this._high = Math.max(steppedValue, this._low);
      }
      
      this.updateSlider();
      this.dispatchSliderEvent();
  }
  
  /**
   * 鼠标释放事件处理
   */
  handleMouseUp() {
      this.endDrag();
  }
  
  /**
   * 触摸结束事件处理
   */
  handleTouchEnd() {
      this.endDrag();
  }
  
  /**
   * 结束拖拽
   */
  endDrag() {
      if (this.isDragging) {
          this.isDragging = false;
          if (this.activeThumb) {
              this.activeThumb.classList.remove('dragging');
              this.activeThumb = null;
          }
      }
  }
  
  /**
   * 更新滑块位置和显示
   */
  updateSlider() {
      const lowPercent = ((this._low - this._min) / (this._max - this._min)) * 100;
      const highPercent = ((this._high - this._min) / (this._max - this._min)) * 100;
      
             if (this.lowThumb) {
           this.lowThumb.style.left = `${lowPercent}%`;
           if (this.lowLabel) {
               this.lowLabel.textContent = this.#formatCurrency(this._low);
           }
       }
       
       if (this.highThumb) {
           this.highThumb.style.left = `${highPercent}%`;
           if (this.highLabel) {
               this.highLabel.textContent = this.#formatCurrency(this._high);
           }
       }
      
      if (this.range) {
          this.range.style.left = `${lowPercent}%`;
          this.range.style.width = `${highPercent - lowPercent}%`;
      }
  }
  
     /**
    * 派发滑块事件
    */
   dispatchSliderEvent() {
       /** @type {SliderEventDetail} */
       const detail = {
           low: this._low,
           high: this._high,
           min: this._min,
           max: this._max
       };
       
       const event = new CustomEvent('slider', {
           detail,
           bubbles: true
       });
       this.dispatchEvent(event);
   }

   /**
    * 格式化货币显示
    * @param {number} value - 要格式化的数值
    * @returns {string} 格式化后的货币字符串
    */
   #formatCurrency(value) {
       // 简单的货币格式化，可以根据需要调整
       return `$${value.toFixed(0)}`;
   }
  
  // 公共属性访问器
  
  /**
   * 获取最小值
   * @returns {number}
   */
  get low() { 
      return this._low; 
  }
  
  /**
   * 设置最小值
   * @param {number} value - 新的最小值
   */
  set low(value) {
      this._low = Math.max(this._min, Math.min(value, this._high));
      this.updateSlider();
  }
  
  /**
   * 获取最大值
   * @returns {number}
   */
  get high() { 
      return this._high; 
  }
  
  /**
   * 设置最大值
   * @param {number} value - 新的最大值
   */
  set high(value) {
      this._high = Math.min(this._max, Math.max(value, this._low));
      this.updateSlider();
  }
  
  /**
   * 获取范围最小值
   * @returns {number}
   */
  get min() { 
      return this._min; 
  }
  
  /**
   * 设置范围最小值
   * @param {number} value - 新的范围最小值
   */
  set min(value) {
      this._min = value;
      this.updateSlider();
  }
  
  /**
   * 获取范围最大值
   * @returns {number}
   */
  get max() { 
      return this._max; 
  }
  
  /**
   * 设置范围最大值
   * @param {number} value - 新的范围最大值
   */
  set max(value) {
      this._max = value;
      this.updateSlider();
  }
  
  /**
   * 获取步长
   * @returns {number}
   */
  get step() { 
      return this._step; 
  }
  
  /**
   * 设置步长
   * @param {number} value - 新的步长
   */
  set step(value) {
      this._step = value;
  }
  
  /**
   * 直接更新DOM和值的便捷方法
   * @param {number} min - 最小值（低端滑块的值）
   * @param {number} max - 最大值（高端滑块的值）
   */
  updateDomz(min, max) {
      // 验证和调整输入值
      const validMin = Math.max(this._min, Math.min(min, this._max));
      const validMax = Math.max(this._min, Math.min(max, this._max));
      
      // 确保 min <= max
      this._low = Math.min(validMin, validMax);
      this._high = Math.max(validMin, validMax);
      
      // 应用步长约束
      this._low = Math.round(this._low / this._step) * this._step;
      this._high = Math.round(this._high / this._step) * this._step;
      
      // 更新DOM显示
      this.updateSlider();
      
      // 派发事件通知其他组件
      this.dispatchSliderEvent();
      
      console.log(`updateDomz: Set values to low=${this._low}, high=${this._high}`);
  }

  /**
   * 组件从 DOM 断开时的清理
   */
  disconnectedCallback() {
      // 清理全局事件监听器
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      document.removeEventListener('touchmove', this.handleTouchMove);
      document.removeEventListener('touchend', this.handleTouchEnd);
  }
}
// 注册自定义元素
customElements.define('dual-slider', DualSlider);

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();

    // 立即重置双向滑块
    this.#resetDualSlider(/** @type {HTMLElement | null} */ (container));
    
    // 也在延迟后再次重置，确保DOM完全更新
    setTimeout(() => {
      this.#resetDualSlider(/** @type {HTMLElement | null} */ (container));
    }, 300);
  }

  /**
   * Reset dual slider to initial state (0 to max)
   * @param {HTMLElement | null} container - The container element
   */
  #resetDualSlider(container) {
    if (!container) {
      console.log('No container found for slider reset');
      return;
    }
    
    const dualSlider = /** @type {DualSlider | null} */ (container.querySelector('dual-slider'));
    if (dualSlider) {
      console.log('Found dual slider, resetting...');
      
      // 使用新的 updateDomz 方法重置
      if (typeof dualSlider.updateDomz === 'function') {
        dualSlider.updateDomz(0, dualSlider.max);
      } else {
        // 备用方法
        dualSlider.low = 0;
        dualSlider.high = dualSlider.max;
        
        if (typeof dualSlider.updateSlider === 'function') {
          dualSlider.updateSlider();
        }
        
        if (typeof dualSlider.dispatchSliderEvent === 'function') {
          dualSlider.dispatchSliderEvent();
        }
      }
      
      console.log('Slider reset complete:', { 
        low: dualSlider.low, 
        high: dualSlider.high
      });
    } else {
      console.log('Dual slider not found');
      // 尝试通过不同的选择器找到滑块
      const allSliders = document.querySelectorAll('dual-slider');
      console.log('Found sliders in document:', allSliders.length);
    }
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;
 
    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Perform the actual slider reset
   */
  #performSliderReset() {
    const priceFacet = document.querySelector('price-facet-component');
    if (priceFacet) {
      const dualSlider = /** @type {DualSlider | null} */ (priceFacet.querySelector('dual-slider'));
      if (dualSlider) {
        console.log('Removing price filter - resetting slider');
        
        // 使用新的 updateDomz 方法重置
        if (typeof dualSlider.updateDomz === 'function') {
          dualSlider.updateDomz(0, dualSlider.max);
        } else {
          // 备用方法
          dualSlider.low = 0;
          dualSlider.high = dualSlider.max;
          
          if (typeof dualSlider.updateSlider === 'function') {
            dualSlider.updateSlider();
          }
          
          if (typeof dualSlider.dispatchSliderEvent === 'function') {
            dualSlider.dispatchSliderEvent();
          }
        }
        
        console.log('Price filter slider reset complete');
      }
    }
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  /**
   * Updates filter and sorting
   * @param {Object} data - The data object
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(data, event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * @typedef {Object} DualSliderRefs
 * @property {HTMLElement | null} container
 * @property {HTMLElement | null} track
 * @property {HTMLElement | null} range
 * @property {HTMLElement | null} lowThumb
 * @property {HTMLElement | null} highThumb
 * @property {HTMLElement | null} lowLabel
 * @property {HTMLElement | null} highLabel
 * @property {number} trackLeft
 * @property {number} trackWidth
 */

/**
 * @typedef {Object} SliderEventDetail
 * @property {number} low
 * @property {number} high
 * @property {number} min
 * @property {number} max
 */


